import { Text, Element as SlateElement } from 'slate';
import { fontFamilyMap } from './SlateUtilityFunctions';

// DESERIALIZATION: HTML to Slate
const ELEMENT_TAGS = {
    A: (el) => ({ type: 'link', href: el.getAttribute('href'), target: el.getAttribute('target') || '_blank' }),
    BLOCKQUOTE: () => ({ type: 'blockquote' }),
    H1: () => ({ type: 'headingOne' }),
    H2: () => ({ type: 'headingTwo' }),
    H3: () => ({ type: 'headingThree' }),
    H4: () => ({ type: 'headingFour' }),
    H5: () => ({ type: 'headingFive' }),
    H6: () => ({ type: 'headingSix' }),
    IMG: (el) => ({ type: 'image', url: el.getAttribute('src') }),
    LI: () => ({ type: 'list-item' }),
    OL: () => ({ type: 'orderedList' }),
    P: () => ({ type: 'paragraph' }),
    PRE: () => ({ type: 'code' }),
    UL: () => ({ type: 'unorderedList' }),
    TABLE: (el) => {
        try {
            // Check if table has explicit header tags
            const hasExplicitHeader = el.querySelector('thead') || el.querySelector('th');

            // ✅ FIX: If no explicit headers, we'll convert first row to headers
            // This is handled in the TR processing below
            return { type: hasExplicitHeader ? 'table' : 'table' }; // Always use 'table' type
        } catch (error) {
            console.warn('Error processing table:', error);
            // Return paragraph as fallback for malformed tables
            return { type: 'paragraph' };
        }
    },
    THEAD: () => ({ type: 'table-head' }),
    TBODY: () => ({ type: 'table-body' }),
    TR: () => ({ type: 'table-row' }),
    TD: () => ({ type: 'table-cell' }),
    TH: () => ({ type: 'table-header' }),
    DIV: () => ({ type: 'paragraph' }),
};

const TEXT_TAGS = {
    CODE: () => ({ code: true }),
    DEL: () => ({ strikethrough: true }),
    EM: () => ({ italic: true }),
    I: () => ({ italic: true }),
    S: () => ({ strikethrough: true }),
    STRIKE: () => ({ strikethrough: true }),
    STRONG: () => ({ bold: true }),
    B: () => ({ bold: true }),
    U: () => ({ underline: true }),
    SUP: () => ({ superscript: true }),
    SUB: () => ({ subscript: true }),
    FONT: (el) => {
        const attrs = {};
        const color = el.getAttribute('color');
        if (color) attrs.color = color;
        return attrs;
    },
};

// Parse inline styles from style attribute
const parseStyles = (style) => {
    const styles = {};
    if (!style) return styles;

    try {
        const declarations = style.split(';').filter((decl) => decl.trim());

        declarations.forEach((decl) => {
            const colonIndex = decl.indexOf(':');
            if (colonIndex === -1) return;

            const property = decl.substring(0, colonIndex).trim();
            const value = decl.substring(colonIndex + 1).trim();

            if (!value) return;

            switch (property) {
                case 'color':
                    // Normalize color value (remove quotes, extra spaces)
                    styles.color = value.replace(/['"]/g, '').trim();
                    break;
                case 'background-color':
                    styles.bgColor = value.replace(/['"]/g, '').trim();
                    break;
                case 'font-size':
                    styles.fontSize = value;
                    break;
                case 'font-family':
                    const cleanValue = value.replace(/['"]/g, '');
                    const familyKey = Object.keys(fontFamilyMap).find((key) => fontFamilyMap[key].includes(cleanValue));
                    if (familyKey) {
                        styles.fontFamily = familyKey;
                    } else {
                        styles.fontFamily = cleanValue;
                    }
                    break;
                case 'text-align':
                    styles.textAlign = value;
                    break;
                case 'font-weight':
                    const weight = parseInt(value);
                    if (value === 'bold' || value === 'bolder' || (!isNaN(weight) && weight >= 600)) {
                        styles.bold = true;
                    } else if (value === 'normal' || (!isNaN(weight) && weight < 600)) {
                        styles.bold = false;
                    }
                    break;
                case 'font-style':
                    if (value === 'italic' || value === 'oblique') {
                        styles.italic = true;
                    }
                    break;
                case 'text-decoration':
                case 'text-decoration-line':
                    if (value.includes('underline')) {
                        styles.underline = true;
                    }
                    if (value.includes('line-through')) {
                        styles.strikethrough = true;
                    }
                    break;
            }
        });

        return styles;
    } catch (error) {
        console.error('Error parsing styles:', error);
        return {};
    }
};

const decodeHtmlEntities = (text) => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
};

// Helper function to get default child for table elements
const getDefaultTableChild = (type) => {
    switch (type) {
        case 'table':
        case 'tableWithoutHeaders':
            return createElementNode('table-body', {}, [
                createElementNode('table-row', {}, [createElementNode('table-cell', {}, [createTextNode('')])]),
            ]);
        case 'table-head':
        case 'table-body':
            return createElementNode('table-row', {}, [createElementNode('table-cell', {}, [createTextNode('')])]);
        case 'table-row':
            return createElementNode('table-cell', {}, [createTextNode('')]);
        default:
            return createTextNode('');
    }
};

// Helper function to extract text from children nodes
const extractTextFromChildren = (children) => {
    if (!children || !Array.isArray(children)) return '';

    return children
        .map((child) => {
            if (child && child.text !== undefined) {
                return child.text;
            }
            if (child && child.children) {
                return extractTextFromChildren(child.children);
            }
            return '';
        })
        .join('');
};

// CRITICAL FIX: Create objects that are extensible
const createTextNode = (text, marks = {}) => {
    return {
        text: text || '',
        ...marks,
    };
};

const createElementNode = (type, attributes = {}, children = []) => {
    return {
        type,
        children: children.length > 0 ? children : [createTextNode('')],
        ...attributes,
    };
};

/**
 * Creates empty paragraph node
 * @returns {Object} Empty paragraph element
 */
const createEmptyParagraph = () => ({
    type: 'paragraph',
    children: [{ text: '' }],
});

/**
 * Wraps content in paragraph if not already block-level
 * @param {Array|Object} content - Content to wrap
 * @param {Object} attrs - Optional paragraph attributes (textAlign, etc.)
 * @returns {Object} Paragraph element
 */
const wrapInParagraph = (content, attrs = {}) => {
    const children = Array.isArray(content) ? content : [content];
    return {
        type: 'paragraph',
        children,
        ...attrs,
    };
};

/**
 * Ensures node children are block-level (wraps text nodes in paragraphs)
 * Used for table cells and list items which require paragraph children
 * Handles mixed arrays by grouping consecutive text nodes into paragraph elements
 * @param {Array} children - Array of child nodes
 * @returns {Array} Children with text nodes wrapped in paragraphs
 */
const ensureParagraphChildren = (children) => {
    if (!children || children.length === 0) {
        return [createEmptyParagraph()];
    }

    // Check if all children are text nodes (simple case)
    const allTextNodes = children.every((child) => child.text !== undefined);
    if (allTextNodes) {
        return [wrapInParagraph(children)];
    }

    // Check if all children are already block-level elements (no text nodes)
    const hasTextNodes = children.some((child) => child.text !== undefined);
    if (!hasTextNodes) {
        return children;
    }

    // Mixed array: group consecutive text nodes into paragraphs
    const result = [];
    let textBuffer = [];

    const flushTextBuffer = () => {
        if (textBuffer.length > 0) {
            result.push(wrapInParagraph(textBuffer));
            textBuffer = [];
        }
    };

    for (const child of children) {
        if (child.text !== undefined) {
            // Accumulate text nodes
            textBuffer.push(child);
        } else {
            // Non-text element: flush any accumulated text nodes first
            flushTextBuffer();
            // Push the element node as-is
            result.push(child);
        }
    }

    // Flush any remaining text nodes at the end
    flushTextBuffer();

    return result;
};

/**
 * Handles BR tags by splitting paragraphs at BR markers
 * @private
 * @param {Array} validChildren - Children nodes that may contain BR markers
 * @param {Object} attrs - Paragraph attributes (textAlign, etc.)
 * @returns {Object|Array} Single paragraph or array of paragraphs
 */
const handleParagraphWithBRMarkers = (validChildren, attrs) => {
    const hasBrMarkers = validChildren.some((child) => child.__BR_MARKER__);

    if (!hasBrMarkers) {
        return wrapInParagraph(validChildren, attrs);
    }

    const paragraphs = [];
    let currentParagraphChildren = [];
    const paragraphAttrs = { type: 'paragraph' };
    if (attrs.textAlign) {
        paragraphAttrs.textAlign = attrs.textAlign;
    }

    validChildren.forEach((child) => {
        if (child.__BR_MARKER__) {
            if (currentParagraphChildren.length > 0) {
                paragraphs.push(wrapInParagraph(currentParagraphChildren, paragraphAttrs));
            } else {
                const emptyPara = createEmptyParagraph();
                paragraphs.push({ ...emptyPara, ...paragraphAttrs });
            }
            currentParagraphChildren = [];
        } else {
            currentParagraphChildren.push(child);
        }
    });

    if (currentParagraphChildren.length > 0) {
        paragraphs.push(wrapInParagraph(currentParagraphChildren, paragraphAttrs));
    }

    if (paragraphs.length > 0) {
        return paragraphs;
    }
    const emptyPara = createEmptyParagraph();
    return { ...emptyPara, ...paragraphAttrs };
};

/**
 * Validates and normalizes list children (must be list-items)
 * @private
 * @param {Array} validChildren - Children nodes
 * @returns {Array} Array of list-item nodes
 */
const normalizeListChildren = (validChildren) => {
    const listItems = validChildren
        .filter((child) => child.type === 'list-item' || child.type === 'paragraph')
        .map((child) => {
            if (child.type === 'paragraph') {
                return createElementNode('list-item', {}, child.children);
            }
            return child;
        });

    return listItems.length > 0 ? listItems : [createElementNode('list-item', {}, [createTextNode('')])];
};

/**
 * Ensures list-item has proper paragraph children
 * @private
 * @param {Array} validChildren - Children nodes
 * @returns {Array} Children with proper paragraph wrapping
 */
const normalizeListItemChildren = (validChildren) => {
    const hasBlockChildren = validChildren.some((child) => child.type !== undefined && child.text === undefined);
    if (!hasBlockChildren) {
        return [wrapInParagraph(validChildren)];
    }
    return validChildren;
};

/**
 * Extracts lists from inside paragraphs (invalid structure)
 * Returns array of separate nodes or null if no lists found
 * @private
 * @param {Array} sanitizedChildren - Children nodes
 * @param {Function} hasTextContent - Function to check if node has text
 * @returns {Array|null} Array of separate nodes or null
 */
const extractListsFromParagraph = (sanitizedChildren, hasTextContent) => {
    const listsInParagraph = [];
    const nonListChildren = [];

    for (const child of sanitizedChildren) {
        if (child.type === 'orderedList' || child.type === 'unorderedList') {
            listsInParagraph.push(child);
        } else {
            nonListChildren.push(child);
        }
    }

    if (listsInParagraph.length > 0) {
        const result = [];
        if (nonListChildren.length > 0 && hasTextContent({ children: nonListChildren })) {
            result.push(wrapInParagraph(nonListChildren));
        }
        result.push(...listsInParagraph);
        return result.length > 0 ? result : null;
    }

    return null;
};

// Deserialize HTML to Slate nodes
const deserialize = (el, markAttributes = {}) => {
    try {
        if (el.nodeType === Node.TEXT_NODE) {
            const text = el.textContent || '';

            if (text === '') {
                return null;
            }

            const hasFormatting = Object.keys(markAttributes).length > 0;
            if (text.trim() === '' && text !== ' ' && !hasFormatting) {
                return null;
            }

            return createTextNode(text, markAttributes);
        }

        if (el.nodeType !== Node.ELEMENT_NODE) {
            return null;
        }

        const nodeName = el.nodeName;

        // Handle BR tags - return a special marker to indicate line break
        if (nodeName === 'BR') {
            return { __BR_MARKER__: true };
        }

        // Handle PRE > CODE structure
        if (nodeName === 'PRE' && el.childNodes[0] && el.childNodes[0].nodeName === 'CODE') {
            const codeContent = el.childNodes[0].textContent || '';
            return createElementNode('code', {}, [createTextNode(codeContent, markAttributes)]);
        }

        // Accumulate marks from current element
        let currentMarks = { ...markAttributes };

        // Check if parent marks have inline styles (color, bgColor, etc.) but no explicit bold
        // This indicates we're inside a styled element from Google Docs/Word
        const parentHasStyles = markAttributes.color || markAttributes.bgColor || markAttributes.fontSize;
        const parentHasBold = markAttributes.bold !== undefined;

        // Process text formatting tags (including FONT)
        if (TEXT_TAGS[nodeName]) {
            // For STRONG/B tags: if we're inside a styled parent without explicit bold,
            // ignore the STRONG tag (it's likely spurious from Google Docs)
            if ((nodeName === 'STRONG' || nodeName === 'B') && parentHasStyles && !parentHasBold) {
                // Skip the STRONG tag - parent has inline styles but didn't specify font-weight
                // This means the source (Google Docs) didn't want it bold
            } else {
                const textAttrs = TEXT_TAGS[nodeName](el);
                currentMarks = { ...currentMarks, ...textAttrs };
            }
        }

        // Process SPAN with inline styles (in addition to any TEXT_TAGS)
        const elementStyle = el.getAttribute('style');
        if (nodeName === 'SPAN' && elementStyle) {
            const inlineStyles = parseStyles(elementStyle);
            currentMarks = { ...currentMarks, ...inlineStyles };
        }

        // Clean up marks: remove properties that are explicitly false (like bold: false)
        // In Slate, we omit properties instead of setting them to false
        Object.keys(currentMarks).forEach((key) => {
            if (currentMarks[key] === false) {
                delete currentMarks[key];
            }
        });

        // Process children with accumulated marks
        const processedChildren = [];

        Array.from(el.childNodes).forEach((child) => {
            const result = deserialize(child, currentMarks);
            if (result) {
                if (Array.isArray(result)) {
                    processedChildren.push(...result);
                } else {
                    processedChildren.push(result);
                }
            }
        });

        // Handle block elements
        if (ELEMENT_TAGS[nodeName]) {
            const attrs = ELEMENT_TAGS[nodeName](el);

            // Add text alignment from styles
            const blockStyles = parseStyles(el.getAttribute('style'));
            if (blockStyles.textAlign) {
                attrs.textAlign = blockStyles.textAlign;
            }

            // Ensure we have valid children
            let validChildren = processedChildren.length > 0 ? processedChildren : [createTextNode('', currentMarks)];

            switch (attrs.type) {
                case 'image':
                    return createElementNode(
                        'image',
                        {
                            url: el.getAttribute('src'),
                            alt: el.getAttribute('alt') || '',
                            width: el.getAttribute('width'),
                            height: el.getAttribute('height'),
                        },
                        [createTextNode('')],
                    );

                case 'link':
                    // Links are inline elements and should only have text children, not paragraphs
                    // If children contain paragraphs, flatten them to text nodes
                    const linkChildren = validChildren.flatMap((child) => {
                        if (child.type === 'paragraph' && child.children) {
                            // Extract text nodes from paragraph
                            return child.children;
                        }
                        return child;
                    });

                    return createElementNode(
                        'link',
                        {
                            url: el.getAttribute('href'),
                            target: el.getAttribute('target') || '_blank',
                        },
                        linkChildren.length > 0 ? linkChildren : [createTextNode('')],
                    );

                case 'unorderedList':
                case 'orderedList':
                    // For lists, ensure children are list items
                    validChildren = normalizeListChildren(validChildren);
                    return createElementNode(attrs.type, attrs, validChildren);

                case 'list-item':
                    // Ensure list item has proper paragraph children
                    validChildren = normalizeListItemChildren(validChildren);
                    return createElementNode(attrs.type, attrs, validChildren);

                case 'paragraph':
                    // Handle BR markers - split into multiple paragraphs
                    return handleParagraphWithBRMarkers(validChildren, attrs);

                case 'table':
                case 'tableWithoutHeaders':
                case 'table-head':
                case 'table-body':
                case 'table-row':
                case 'table-header':
                case 'table-cell':
                    // Handle table elements with extra validation
                    try {
                        // Ensure table elements have valid children
                        if (validChildren.length === 0) {
                            // For cells, add empty text node
                            if (attrs.type === 'table-cell' || attrs.type === 'table-header') {
                                validChildren = [createTextNode('')];
                            } else {
                                // For structural elements, add appropriate child
                                const defaultChild = getDefaultTableChild(attrs.type);
                                validChildren = [defaultChild];
                            }
                        }
                        return createElementNode(attrs.type, attrs, validChildren);
                    } catch (error) {
                        console.warn('Error processing table element:', attrs.type, error);
                        // Convert malformed table content to paragraphs
                        const textContent = extractTextFromChildren(validChildren);
                        return wrapInParagraph(createTextNode(textContent));
                    }

                default:
                    return createElementNode(attrs.type, attrs, validChildren);
            }
        }

        // For formatting elements (including SPAN and FONT with styles), return the processed children
        const hasInlineStyles = nodeName === 'SPAN' && Object.keys(parseStyles(el.getAttribute('style'))).length > 0;
        const isFontTag = nodeName === 'FONT';

        if (TEXT_TAGS[nodeName] || hasInlineStyles || isFontTag) {
            return processedChildren;
        }

        // For unknown elements, wrap children in a paragraph
        if (processedChildren.length > 0) {
            // If we have text nodes with marks, preserve them
            const hasTextNodes = processedChildren.some((child) => child.text !== undefined);
            if (hasTextNodes) {
                return wrapInParagraph(processedChildren);
            }
            return processedChildren;
        }

        return null;
    } catch (error) {
        console.error('Error deserializing node:', el.nodeName, error);
        // Return plain text as fallback
        const text = el.textContent || '';
        if (text.trim()) {
            return createTextNode(text, markAttributes);
        }
        return null;
    }
};

// SERIALIZATION: Slate to HTML
const serialize = (node) => {
    // Handle __BR_MARKER__ objects (used for line breaks in lists)
    if (node && node.__BR_MARKER__) {
        return '<br/>';
    }

    if (Text.isText(node)) {
        let string = escapeHtml(node.text);

        if (node.mention && node.mentionUserId) {
            string = `<span class="text-editor-mention" data-mention-user-id="${node.mentionUserId || ''}">${string}</span>`;
        }

        if (node.bold) {
            string = `<strong>${string}</strong>`;
        }
        if (node.italic) {
            string = `<em>${string}</em>`;
        }
        if (node.underline) {
            string = `<u>${string}</u>`;
        }
        if (node.code) {
            string = `<code>${string}</code>`;
        }
        if (node.superscript) {
            string = `<sup>${string}</sup>`;
        }
        if (node.subscript) {
            string = `<sub>${string}</sub>`;
        }

        // Apply inline styles
        const styles = [];
        if (node.color) styles.push(`color: ${node.color}`);
        if (node.bgColor) styles.push(`background-color: ${node.bgColor}`);
        if (node.fontSize) styles.push(`font-size: ${node.fontSize}`);
        if (node.fontFamily && fontFamilyMap[node.fontFamily]) {
            styles.push(`font-family: ${fontFamilyMap[node.fontFamily]}`);
        }

        if (styles.length > 0) {
            string = `<span style="${styles.join('; ')}">${string}</span>`;
        }

        // Apply strikethrough using simple text-decoration (PDF-compatible)
        if (node.strikethrough) {
            string = `<s style="text-decoration: line-through;">${string}</s>`;
        }

        return string;
    }

    const children = node.children.map((n) => serialize(n)).join('');

    switch (node.type) {
        case 'paragraph':
            const isEmpty = node.children.length === 1 && node.children[0].text === '' && !node.children[0].mention;
            const alignStyle = node.textAlign ? ` style="text-align: ${node.textAlign}"` : '';

            // Preserve empty paragraphs as <div> with non-breaking space for proper spacing
            // This ensures spacing is maintained during copy-paste roundtrip
            if (isEmpty) {
                return `<div${alignStyle}>&nbsp;</div>`;
            }
            return `<div${alignStyle}>${children}</div>`;
        case 'headingOne':
            return `<h1${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h1>`;
        case 'headingTwo':
            return `<h2${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h2>`;
        case 'headingThree':
            return `<h3${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h3>`;
        case 'headingFour':
            return `<h4${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h4>`;
        case 'headingFive':
            return `<h5${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h5>`;
        case 'headingSix':
            return `<h6${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h6>`;
        case 'blockquote':
            return `<blockquote${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</blockquote>`;
        case 'unorderedList':
            return `<ul style="padding-left: 24px; list-style-type: disc; margin-bottom: 8px;">${children}</ul>`;
        case 'orderedList':
            return `<ol style="padding-left: 24px; list-style-type: decimal; margin-bottom: 8px;">${children}</ol>`;
        case 'list-item':
            return `<li${
                node.textAlign ? ` style="text-align: ${node.textAlign}; margin-bottom: 4px;"` : ' style="margin-bottom: 4px;"'
            }>${children}</li>`;
        case 'link':
            const href = node.href || node.url || '#';
            const target = node.target ? ` target="${node.target}"` : '';
            return `<a href="${escapeHtml(href)}"${target}>${children}</a>`;
        case 'table':
        case 'tableWithoutHeaders':
            return `<table style="width: 100%; border-collapse: collapse;">${children}</table>`;
        case 'table-head':
            return `<thead>${children}</thead>`;
        case 'table-body':
            return `<tbody>${children}</tbody>`;
        case 'table-row':
            return `<tr>${children}</tr>`;
        case 'table-header':
            const headerAlign = node.textAlign ? ` style="text-align: ${node.textAlign}"` : '';
            return `<th${headerAlign}>${children}</th>`;
        case 'table-cell':
            const cellAlign = node.textAlign ? ` style="text-align: ${node.textAlign}"` : '';
            return `<td${cellAlign}>${children}</td>`;
        case 'image':
            return `<img src="${escapeHtml(node.url)}" />`;
        case 'code':
            return `<pre><code>${children}</code></pre>`;
        default:
            return children;
    }
};

// Helper function to escape HTML and preserve whitespace
const escapeHtml = (unsafe) => {
    if (typeof unsafe !== 'string') return '';

    let escaped = unsafe.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

    // Preserve multiple consecutive spaces by converting to &nbsp;
    // Keep alternating pattern for better word wrapping
    escaped = escaped.replace(/ {2,}/g, (match) => {
        let result = '';
        for (let i = 0; i < match.length; i++) {
            result += i % 2 === 0 ? '&nbsp;' : ' ';
        }
        return result;
    });

    return escaped;
};

// Public API functions

/**
 * Converts Slate value to HTML string
 * @param {Array} value - Slate document value
 * @returns {string} HTML representation
 */
export const slateToHtml = (value) => {
    try {
        if (!value || !Array.isArray(value)) {
            return '';
        }
        // Join with newlines to preserve spacing between top-level elements
        // This helps maintain proper structure during copy-paste roundtrip
        return value.map((node) => serialize(node)).join('\n');
    } catch (error) {
        console.error('Error serializing Slate to HTML:', error);
        return '';
    }
};

/**
 * Converts HTML string to Slate value with formatting preservation
 * @param {string} html - HTML string to parse
 * @returns {Array} Slate document value
 */
export const htmlToSlate = (html) => {
    try {
        if (!html || html.trim() === '') {
            return [createEmptyParagraph()];
        }

        const document = new DOMParser().parseFromString(html, 'text/html');
        let fragment = [];

        // Process each child node of the body
        Array.from(document.body.childNodes).forEach((node) => {
            const result = deserialize(node);
            if (result) {
                if (Array.isArray(result)) {
                    fragment.push(...result);
                } else {
                    fragment.push(result);
                }
            }
        });

        // Filter out null/undefined values
        fragment = fragment.filter(Boolean);

        // Ensure we return a valid Slate value
        if (fragment.length === 0) {
            return [createEmptyParagraph()];
        }

        // Ensure all items are proper element nodes
        return fragment.map((item) => {
            if (item.type) {
                return item;
            } else if (item.text !== undefined) {
                // Wrap standalone text nodes in paragraphs
                return wrapInParagraph(item);
            }
            return createEmptyParagraph();
        });
    } catch (error) {
        console.error('Error deserializing HTML to Slate:', error);
        return [createEmptyParagraph()];
    }
};

/**
 * Extracts plain text from Slate value (no formatting)
 * @param {Array} value - Slate document value
 * @returns {string} Plain text content
 */
export const slateToPlainText = (value) => {
    try {
        if (!value || !Array.isArray(value)) return '';

        const extractText = (node) => {
            if (Text.isText?.(node)) {
                return node.text || '';
            } else if (node.children && Array.isArray(node.children)) {
                return node.children.map(extractText).join('');
            }
            return '';
        };

        return value.map(extractText).join('\n');
    } catch (error) {
        console.error('Error converting Slate to plain text:', error);
        return '';
    }
};

/**
 * Converts plain text to Slate value (one paragraph per line)
 * @param {string} text - Plain text to convert
 * @returns {Array} Slate document value
 */
export const plainTextToSlate = (text) => {
    if (!text || text.trim() === '') {
        return [createEmptyParagraph()];
    }

    const lines = text.split('\n');
    return lines.map((line) => wrapInParagraph(createTextNode(line)));
};

/**
 * Removes trailing and leading empty paragraphs from Slate value
 * @param {Array|undefined|null} value - Slate document value (can be undefined)
 * @returns {Array} Cleaned value
 */
export const cleanTrailingNewlines = (value) => {
    if (!value || !Array.isArray(value) || value.length === 0) {
        return [createEmptyParagraph()];
    }
    let cleanedValue = [...value];
    const isEmptyParagraph = (node) => {
        return (
            node &&
            node?.type === 'paragraph' &&
            node?.children &&
            node?.children?.length === 1 &&
            node?.children?.[0] &&
            node?.children?.[0]?.text === ''
        );
    };
    while (cleanedValue.length > 0 && isEmptyParagraph(cleanedValue[cleanedValue.length - 1])) {
        cleanedValue.pop();
    }
    while (cleanedValue.length > 0 && isEmptyParagraph(cleanedValue[0])) {
        cleanedValue.shift();
    }
    return cleanedValue.length > 0 ? cleanedValue : [createEmptyParagraph()];
};

/**
 * Helper to recursively check if a node has any text content
 */
const hasTextContent = (node) => {
    if (!node) return false;
    if (node.text !== undefined && node.text.trim()) return true;
    if (node.children && Array.isArray(node.children)) {
        return node.children.some(hasTextContent);
    }
    return false;
};

/**
 * Recursively sanitizes a single Slate node
 * Removes BR markers, unwraps table-body, ensures proper nesting
 * @private
 * @param {Object} node - Node to sanitize
 * @returns {Object|Array|null} Sanitized node(s) or null
 */
const sanitizeNode = (node) => {
    // Skip null/undefined nodes
    if (!node) return null;

    // Remove __BR_MARKER__ objects - they should not appear in stored data
    if (node.__BR_MARKER__) {
        return null;
    }

    // Handle text nodes - ensure they have a text property
    if (node.text !== undefined) {
        return { ...node };
    }

    // Handle element nodes
    if (node.type) {
        let sanitizedChildren = [];

        // Process children if they exist
        if (node.children && Array.isArray(node.children)) {
            // For table-body nodes, unwrap and return the children directly
            if (node.type === 'table-body') {
                // Return the children of table-body to be merged into parent
                // Flatten in case any child sanitization returns arrays
                return node.children.map(sanitizeNode).flat().filter(Boolean);
            }

            // Recursively sanitize children - use reverse iteration to avoid path invalidation
            for (let i = node.children.length - 1; i >= 0; i--) {
                const child = node.children[i];
                const sanitized = sanitizeNode(child);
                if (sanitized) {
                    // If sanitizeNode returned an array (from table-body unwrapping or list extraction), flatten it
                    if (Array.isArray(sanitized)) {
                        sanitizedChildren.unshift(...sanitized);
                    } else {
                        sanitizedChildren.unshift(sanitized);
                    }
                }
            }
        }

        // CRITICAL FIX: Handle paragraphs that contain lists (invalid structure)
        // Extract lists from paragraphs and return them separately
        if (node.type === 'paragraph') {
            const extracted = extractListsFromParagraph(sanitizedChildren, hasTextContent);
            if (extracted) {
                return extracted;
            }
        }

        // Ensure element nodes have at least one child
        if (sanitizedChildren.length === 0) {
            // table = table with headers (first row as header); tableWithoutHeaders = no header row.
            // No separate 'tableWithHeaders' type in this codebase.
            const tableRelatedTypes = [
                'table',
                'tableWithoutHeaders',
                'table-row',
                'table-head',
                'table-body',
                'table-cell',
                'table-header',
            ];
            if (tableRelatedTypes.includes(node.type)) {
                sanitizedChildren = [getDefaultTableChild(node.type)];
            } else if (['image', 'video', 'embed'].includes(node.type)) {
                sanitizedChildren = [{ text: '' }];
            } else {
                sanitizedChildren = [{ text: '' }];
            }
        }

        // Ensure table cells and headers have proper paragraph children
        if (node.type === 'table-cell' || node.type === 'table-header') {
            sanitizedChildren = ensureParagraphChildren(sanitizedChildren);
        }

        // Ensure list-items have proper paragraph children
        if (node.type === 'list-item') {
            sanitizedChildren = ensureParagraphChildren(sanitizedChildren);
        }

        // Ensure lists only contain list-item children (convert paragraphs and text nodes to list-items)
        if (node.type === 'orderedList' || node.type === 'unorderedList') {
            sanitizedChildren = sanitizedChildren
                .map((child) => {
                    // Keep existing list-items as-is
                    if (child.type === 'list-item') return child;

                    // Convert paragraphs with content to list-items
                    if (child.type === 'paragraph' && hasTextContent(child)) {
                        return {
                            type: 'list-item',
                            children: [child],
                        };
                    }

                    // Convert bare text nodes to list-items with paragraph wrapper
                    if (child.text !== undefined) {
                        return {
                            type: 'list-item',
                            children: [wrapInParagraph(child)],
                        };
                    }

                    // Filter out other invalid nodes
                    return null;
                })
                .filter(Boolean); // Remove null entries

            // Ensure at least one list-item
            if (sanitizedChildren.length === 0) {
                sanitizedChildren = [{ type: 'list-item', children: [createEmptyParagraph()] }];
            }
        }

        // Create sanitized node without __BR_MARKER__ or other invalid properties
        const { __BR_MARKER__, ...cleanNode } = node;
        return {
            ...cleanNode,
            children: sanitizedChildren,
        };
    }

    // Unknown node type - try to convert to paragraph if it has text content
    if (typeof node === 'object') {
        // It's an invalid object, skip it
        console.warn('Skipping invalid Slate node:', node);
        return null;
    }

    return null;
};

/**
 * Sanitizes Slate value to ensure it's valid for the editor
 * Removes BR markers, unwraps table-body nodes, ensures proper structure
 * @param {Array} value - The Slate value array to sanitize
 * @returns {Array} Sanitized Slate value
 */
export const sanitizeSlateValue = (value) => {
    if (!value || !Array.isArray(value)) {
        return [createEmptyParagraph()];
    }

    // Sanitize each top-level node
    const sanitized = value
        .map(sanitizeNode)
        .filter(Boolean)
        .flatMap((node) => (Array.isArray(node) ? node : [node])); // Flatten any arrays from table-body unwrapping or list extraction from paragraphs

    // Ensure we have at least one valid element
    if (sanitized.length === 0) {
        return [createEmptyParagraph()];
    }

    // Ensure all top-level nodes are element nodes (have a type property)
    return sanitized.map((node) => {
        if (node.type) {
            return node;
        }
        // Wrap text nodes in paragraphs
        if (node.text !== undefined) {
            return wrapInParagraph(node);
        }
        // Invalid node, return empty paragraph
        return createEmptyParagraph();
    });
};

export default {
    slateToHtml,
    htmlToSlate,
    slateToPlainText,
    plainTextToSlate,
    sanitizeSlateValue,
};
